import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminEmergencyReset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, KeyRound, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const AdminForgotPasswordPage = () => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Recovery code आवश्यक है");
      return;
    }
    setLoading(true);
    try {
      const res = await adminEmergencyReset(code.trim());
      setResult(res);
      toast.success("Password reset हो गया!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid recovery code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-0px)] flex items-center justify-center px-4 py-10" data-testid="admin-forgot-page">
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
        <Link to="/admin/login" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#2D5A27] mb-4" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center mb-4">
          <KeyRound className="w-7 h-7 text-white" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-[#1E293B]">Forgot Password</h1>
        <p className="text-sm text-slate-500 mt-1">
          Reset admin credentials using your Recovery Code
        </p>

        {result ? (
          <div className="mt-6 p-5 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="reset-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <p className="font-heading font-semibold text-emerald-900">Reset Successful!</p>
            </div>
            <p className="text-sm text-emerald-900 mb-3">आपका new admin login:</p>
            <div className="p-3 rounded-md bg-white border border-emerald-200 font-mono text-sm">
              <p><strong>Username:</strong> <span data-testid="reset-username">{result.username}</span></p>
              <p><strong>Password:</strong> <span data-testid="reset-password">{result.password}</span></p>
            </div>
            <p className="text-xs text-emerald-800 mt-3">
              ⚠️ Login होते ही Settings → "Change Admin Login" पर जाकर तुरंत नया password सेट करें।
            </p>
            <Button
              onClick={() => navigate("/admin/login")}
              data-testid="go-to-login-btn"
              className="w-full mt-4 rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold"
            >
              Go to Login
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 flex gap-2 text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Recovery code आपके Emergent backend .env में <code className="bg-amber-100 px-1 rounded">ADMIN_TOKEN_SECRET</code> value होता है। यह code shop owner के पास ही रहता है।
              </p>
            </div>
            <form onSubmit={submit} className="mt-4 space-y-4">
              <div>
                <Label className="text-sm font-medium">Recovery Code</Label>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="recovery-code-input"
                  placeholder="Paste your recovery code here"
                  className="mt-1.5 h-11 bg-white font-mono text-sm"
                  autoFocus
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full h-12 rounded-md bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminForgotPasswordPage;
